import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, Lock, MessageCircle, NotebookText } from 'lucide-react';
import { AvatarView } from '../../components/ui/AvatarView';
import { EmptyState } from '../../components/ui/EmptyState';
import { PublicSiteHeader } from '../../components/PublicSiteHeader';
import { PublicSiteFooter } from '../../components/PublicSiteFooter';
import { PoweredByZmistBadge } from '../../components/ui/PoweredByZmistBadge';
import { PublicReportBody } from '../../components/reports/PublicReportBody';
import { SignalActivityChart } from '../../components/charts/SignalActivityChart';
import { BunchedMessageGroup } from '../../components/signal/BunchedMessageGroup';
import { bunchMessages } from '../../lib/bunchMessages';
import {
  fetchPublicChannel,
  fetchPublicReportByNumber,
  fetchReportCheckpoint,
  fetchTemplates,
  type FetchedSignal,
  type PublicChannelView,
  type PublicReport,
} from '../../api/dataApi';
import { REPORT_TYPE, type ChartVariant, type Message, type Template } from '../../types';

const VALID_CHART_VARIANTS: ChartVariant[] = ['number', 'bar', 'line', 'circle'];
function asChartVariant(value: string): ChartVariant {
  return (VALID_CHART_VARIANTS as string[]).includes(value) ? (value as ChartVariant) : 'bar';
}

const ROW_DIVIDER = 'border-black/[0.06] dark:border-white/[0.08]';

// GET /public/channels/{nickname}/reports/{number}'s screen - a single
// report's stable permalink (channel/public_report_by_number.wsl),
// distinct from PublicChannelPageScreen.tsx's Reports tab (always the
// latest). This page's Messages/Charts tabs are NOT live - they read
// channel/public_report_checkpoint.wsl, the channel's messages frozen at
// the moment this report posted (internal/report.PostReport's checkpoint
// capture). Everything here is permanent historical record; "View
// current live data" is the one way back to the present.
export function PublicReportScreen() {
  const { nickname, number } = useParams();
  const [view, setView] = useState<PublicChannelView | null>(null);
  const [report, setReport] = useState<PublicReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [checkpointFound, setCheckpointFound] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<FetchedSignal[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [rawDataOpen, setRawDataOpen] = useState(false);
  const [chartType, setChartType] = useState<ChartVariant>('bar');

  useEffect(() => {
    if (!nickname || !number) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const [channelView, foundReport] = await Promise.all([
          fetchPublicChannel(nickname),
          fetchPublicReportByNumber(nickname, Number(number)),
        ]);
        if (cancelled) return;
        setView(channelView);
        setReport(foundReport);
        setChartType(asChartVariant(channelView.viewChartType));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load this channel.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nickname, number]);

  useEffect(() => {
    if (!nickname || !number) return;
    let cancelled = false;
    (async () => {
      // Same bounded full-page-loop shape as PublicChannelPageScreen.tsx's
      // live fetchSignals loop - here reading a checkpoint's frozen
      // records instead, capped the same way the checkpoint itself was
      // capped at capture time (internal/report.maxCheckpointRecords).
      const PAGE_SIZE = 100;
      const MAX_PAGES = 50;
      let offset = 0;
      let all: FetchedSignal[] = [];
      let found = false;
      for (let page = 0; page < MAX_PAGES; page++) {
        const checkpointPage = await fetchReportCheckpoint(nickname, Number(number), offset, PAGE_SIZE);
        if (cancelled) return;
        found = checkpointPage.found;
        all = all.concat(checkpointPage.signals);
        if (!checkpointPage.pagination.hasMore || checkpointPage.signals.length === 0) break;
        offset += checkpointPage.pagination.limit || PAGE_SIZE;
      }
      const templatesPage = await fetchTemplates();
      if (cancelled) return;
      setCheckpointFound(found);
      setMessages(all.sort((a, b) => (a.date < b.date ? 1 : -1)));
      setTemplates(templatesPage?.templates ?? []);
    })()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setContentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nickname, number]);

  const nonReportMessages = useMemo(() => messages.filter((m) => m.type !== REPORT_TYPE), [messages]);
  const messageGroups = useMemo(
    () => bunchMessages(nonReportMessages, view?.bunchingInterval ?? 0),
    [nonReportMessages, view?.bunchingInterval]
  );
  const chartMessages: Message[] = useMemo(
    () =>
      messages.map((m) => ({
        id: m.id,
        sender: m.sender,
        text: m.text,
        date: m.date,
        isMine: false,
        type: !m.type || m.type === 'message' ? undefined : m.type,
        value: m.value,
      })),
    [messages]
  );

  const toggleRawData = () => setRawDataOpen((open) => !open);

  function renderMessagesList() {
    if (contentLoading) {
      return (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <Loader2 size={28} className="animate-spin text-black/40 dark:text-white/40" />
          <span className="text-sm text-black/50 dark:text-white/50">Loading raw data…</span>
        </div>
      );
    }
    if (checkpointFound === false) {
      return (
        <EmptyState
          icon={MessageCircle}
          title="No historical record"
          subtitle="This report predates checkpoints, or its historical data couldn't be captured."
        />
      );
    }
    if (nonReportMessages.length === 0) {
      return <EmptyState icon={MessageCircle} title="No raw data" subtitle="This checkpoint has no messages." />;
    }
    return messageGroups.map((group, i) => (
      <div key={group[0].id} className={i > 0 ? `border-t ${ROW_DIVIDER}` : ''}>
        <BunchedMessageGroup group={group} templates={templates} dividerClassName={ROW_DIVIDER} />
      </div>
    ));
  }

  return (
    <div className="min-h-screen bg-(--color-bg) text-(--color-text) flex flex-col">
      <PublicSiteHeader />
      <div className="flex-1 w-full flex flex-col items-center px-4 py-10 sm:py-14">
        <div className="w-full max-w-[1000px] flex flex-col gap-5">
          {loading && !view && <span className="text-sm text-black/50 dark:text-white/50 text-center">Loading...</span>}

          {!loading && error && !view && <EmptyState icon={Lock} title="This channel isn't available" subtitle={error} />}

          {view && (
            <>
              <div className="flex flex-col items-center text-center gap-2">
                <AvatarView symbol={view.avatarSymbol} colorHex={view.accentHex} imageKey={view.avatarImageKey} size={64} zoomable />
                <h1 className="text-2xl font-bold m-0">{view.title}</h1>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                  <span className="inline-flex items-center rounded-full border border-(--color-accent) text-(--color-accent) text-xs px-3 py-1">
                    Public channel
                  </span>
                  <span className="inline-flex items-center rounded-full bg-black/[0.06] dark:bg-white/10 text-black/60 dark:text-white/60 text-xs px-3 py-1">
                    {view.nickname}
                  </span>
                </div>
                {!view.badgeRemovable && <PoweredByZmistBadge />}
                <Link
                  to={`/public/${nickname}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-(--color-accent) hover:underline mt-1"
                >
                  <ArrowLeft size={14} />
                  View current live data
                </Link>
              </div>

              {report ? (
                <div className="w-full flex flex-col rounded-[20px] bg-white dark:bg-white/5 border border-black/10 dark:border-white/10 shadow-sm">
                  <div className="px-5 pt-5 pb-5">
                    <h2 className="text-lg font-bold m-0">Report #{report.number}</h2>
                    {report.rangeStart && report.rangeEnd && (
                      <p className="text-xs text-black/40 dark:text-white/40 m-0 mt-1">
                        Data captured: {new Date(report.rangeStart).toLocaleDateString()} –{' '}
                        {new Date(report.rangeEnd).toLocaleDateString()}
                      </p>
                    )}
                    <div className="mt-3">
                      <PublicReportBody report={report} />
                    </div>
                  </div>

                  <div className={`px-5 pt-4 pb-5 border-t ${ROW_DIVIDER}`}>
                    <h3 className="text-sm font-semibold text-black/60 dark:text-white/60 mb-3">Charts</h3>
                    {contentLoading ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 size={24} className="animate-spin text-black/30 dark:text-white/30" />
                      </div>
                    ) : checkpointFound === false ? (
                      <EmptyState
                        icon={NotebookText}
                        title="No historical record"
                        subtitle="This report predates checkpoints, or its historical data couldn't be captured."
                      />
                    ) : (
                      <SignalActivityChart
                        messages={chartMessages}
                        templates={templates}
                        chartType={chartType}
                        onChartTypeChange={setChartType}
                        locked={!view.publicChartFiltersAllowed}
                        initialSettings={view.chartSettings}
                        storageKey={`chart.checkpoint.${nickname}.${number}`}
                      />
                    )}
                  </div>

                  <div className={`border-t ${ROW_DIVIDER}`}>
                    <button
                      type="button"
                      onClick={toggleRawData}
                      className="w-full flex items-center justify-between gap-2 px-5 py-4 text-left"
                    >
                      <span className="text-sm font-semibold text-black/60 dark:text-white/60">Raw Data</span>
                      {rawDataOpen ? (
                        <ChevronUp size={16} className="text-black/30 dark:text-white/30" />
                      ) : (
                        <ChevronDown size={16} className="text-black/30 dark:text-white/30" />
                      )}
                    </button>
                    {rawDataOpen && <div className="px-5 pb-5">{renderMessagesList()}</div>}
                  </div>
                </div>
              ) : (
                <EmptyState icon={NotebookText} title="Report not found" subtitle="This report number doesn't exist for this channel." />
              )}
            </>
          )}
        </div>
      </div>
      <PublicSiteFooter />
    </div>
  );
}
